import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { revealField } from '../../Lib/redux/slices/progressiveDisplay';
import {
  getRadioChainDisplayState,
  RadioChainDisplayState,
} from '../../Helpers/progressiveDisplay';
import { ComponentTypes } from '../../Data/Components';

const useProgressiveDisplay = ({ currentPage }) => {
  const dispatch = useDispatch();
  const progressiveDisplay = useSelector((store) => store.progressiveDisplay);
  const components = useSelector((store) => store.components);

  const pageConfig = progressiveDisplay?.pages?.[currentPage];
  // A radios chain is derived from the answers and `reopenedFields` on every
  // render; `revealedFields` is only for text-field chains.
  const isRadiosChain = pageConfig?.inputType === ComponentTypes.RADIOS;

  const getRadioDisplayState = (componentId) =>
    getRadioChainDisplayState({ pageConfig, components, componentId });

  const shouldRevealFields = () => {
    if (!pageConfig || isRadiosChain) return false;
    const { trigger, componentsChain, revealedFields } = pageConfig;

    if (revealedFields.length === componentsChain.length) return false;

    const triggerValue = components[`component_${trigger}`]?.input?.value;
    // Check if trigger has received at least one character input from user
    return triggerValue && triggerValue.length >= 1;
  };

  const revealNextField = () => {
    const { componentsChain, revealedFields } = pageConfig;

    const lastRevealedId = revealedFields[revealedFields.length - 1];
    const lastRevealedValue =
      components[`component_${lastRevealedId}`]?.input?.value;

    if (!lastRevealedValue) return;

    const chainIndex = componentsChain.indexOf(lastRevealedId);
    const nextField = componentsChain[chainIndex + 1];
    if (!nextField || revealedFields.includes(nextField)) return;

    dispatch(revealField({ page: currentPage, componentId: nextField }));
  };

  useEffect(() => {
    if (!shouldRevealFields()) return;

    const { revealedFields, componentsChain } = pageConfig;

    if (revealedFields.length < 1) {
      dispatch(
        revealField({ page: currentPage, componentId: componentsChain[0] })
      );
      return;
    }

    revealNextField();
  }, [pageConfig, components, currentPage]);

  const isInComponentsChain = (componentId) => {
    if (!pageConfig) return false;
    return pageConfig.componentsChain.includes(componentId);
  };

  const isInProgressiveChain = (componentId) =>
    !!pageConfig &&
    (pageConfig.trigger === componentId || isInComponentsChain(componentId));

  const isProgressivelyHidden = (componentId) => {
    if (isRadiosChain) {
      return (
        getRadioDisplayState(componentId) === RadioChainDisplayState.HIDDEN
      );
    }
    return (
      isInComponentsChain(componentId) &&
      !pageConfig.revealedFields.includes(componentId)
    );
  };

  const isProgressiveReveal = (componentId) => {
    if (isRadiosChain) {
      return (
        isInComponentsChain(componentId) &&
        getRadioDisplayState(componentId) === RadioChainDisplayState.CURRENT
      );
    }
    return (
      isInComponentsChain(componentId) &&
      pageConfig.revealedFields.includes(componentId)
    );
  };

  const isProgressivelyCollapsed = (componentId) => {
    if (!isRadiosChain) return false;
    return (
      getRadioDisplayState(componentId) === RadioChainDisplayState.COLLAPSED
    );
  };

  const hasProgressiveChainOnPage = !!(
    pageConfig &&
    Array.isArray(pageConfig.componentsChain) &&
    pageConfig.componentsChain.length > 0
  );

  return {
    isProgressivelyHidden,
    isProgressiveReveal,
    isProgressivelyCollapsed,
    isInProgressiveChain,
    hasProgressiveChainOnPage,
  };
};

export default useProgressiveDisplay;
